import { useCallback, useEffect, useState } from "react";
import { Activity, Bot, Users, CalendarDays, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/adminAuth";

const fmt = (n, dp = 2) =>
  n != null && Number.isFinite(+n)
    ? Number(n).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })
    : "—";

const MiniStat = ({ label, value, accent, testId }) => (
  <div data-testid={testId} className="rounded-lg border border-vts-line bg-vts-bg/40 p-3">
    <div className={`font-mono text-xl tabular-nums font-medium ${accent || "text-white"}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-vts-silver/55 mt-0.5">{label}</div>
  </div>
);

const BreakdownTable = ({ rows, keyField, label, testId }) => {
  const maxVol = Math.max(1, ...rows.map((r) => r.volume));
  return (
    <div data-testid={testId} className="vts-card p-4">
      <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-semibold mb-2 flex items-center gap-1.5">
        <CalendarDays className="h-3 w-3" /> {label}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-vts-silver/50 font-mono">No trades in window</p>
      ) : (
        <div className="space-y-1">
          <div className="grid grid-cols-[80px_1fr_60px_80px_70px] gap-2 text-[9px] uppercase tracking-wider text-vts-silver/45 font-mono pb-1 border-b border-vts-line">
            <span>{keyField === "day" ? "Date" : "Week of"}</span><span>Volume</span><span className="text-right">Trades</span><span className="text-right">Volume $</span><span className="text-right">Markup $</span>
          </div>
          {rows.map((r) => (
            <div key={r[keyField]} className="grid grid-cols-[80px_1fr_60px_80px_70px] gap-2 items-center text-[11px] font-mono">
              <span className="text-vts-silver/70">{(r[keyField] || "").slice(5)}</span>
              <div className="h-2 rounded bg-vts-line/40 overflow-hidden">
                <div className="h-full rounded bg-vts-blue/70" style={{ width: `${Math.max(2, (r.volume / maxVol) * 100)}%` }} />
              </div>
              <span className="text-right text-vts-silver/70">{r.trades}</span>
              <span className="text-right text-white">${fmt(r.volume)}</span>
              <span className={`text-right ${r.markup > 0 ? "text-vts-profit" : "text-vts-silver/45"}`}>${fmt(r.markup)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminAnalyticsPanel = ({ onUnauthorized }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("daily");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/analytics", { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401 && onUnauthorized) onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const a = data?.active || {};

  return (
    <div data-testid="admin-analytics-panel" className="space-y-3 mt-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-semibold text-base text-white tracking-tight flex items-center gap-2">
            <Activity className="h-4 w-4 text-vts-blue" /> Volume &amp; bot analytics
          </h3>
          <div className="text-[11px] text-vts-silver/60 font-mono mt-0.5">
            Who's trading, which bots are running, and where the markup comes from
          </div>
        </div>
        <button data-testid="analytics-refresh-btn" onClick={load}
          className="text-[11px] text-vts-silver/60 hover:text-white p-2 rounded border border-vts-line bg-vts-surface hover:bg-vts-surfaceHover transition flex items-center gap-1.5">
          <RefreshCcw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* ACTIVE / BOT USAGE */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <MiniStat testId="analytics-active-24h" label="Real traders · 24h" value={a.traders_24h ?? "—"} accent="text-vts-profit" />
        <MiniStat testId="analytics-active-7d" label="Real traders · 7d" value={a.traders_7d ?? "—"} accent="text-vts-blue" />
        <MiniStat testId="analytics-logins-24h" label="Logins · 24h" value={a.logins_24h ?? "—"} />
        <MiniStat testId="analytics-over3-running" label="Digit bots live" value={a.over3_bots_running ?? "—"} accent="text-amber-400" />
        <MiniStat testId="analytics-autobots-running" label="Auto bots live" value={a.autobots_running ?? "—"} accent="text-amber-400" />
        <MiniStat testId="analytics-copy-followers" label="Copy followers" value={a.copy_followers ?? "—"} />
      </div>

      {(a.over3_accounts?.length > 0 || a.autobot_accounts?.length > 0) && (
        <div data-testid="analytics-bot-accounts" className="vts-card p-3 flex flex-wrap gap-1.5 items-center">
          <Bot className="h-3.5 w-3.5 text-amber-400" />
          <span className="text-[10px] uppercase tracking-wider text-vts-silver/55 font-mono mr-1">Running now:</span>
          {(a.over3_accounts || []).map((id) => (
            <span key={`o3-${id}`} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">digit · {id}</span>
          ))}
          {(a.autobot_accounts || []).map((id) => (
            <span key={`ab-${id}`} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-vts-blue/10 text-vts-blue border border-vts-blue/30">auto · {id}</span>
          ))}
        </div>
      )}

      {/* DAILY / WEEKLY TOGGLE */}
      <div className="flex items-center gap-1 bg-vts-surface/60 border border-vts-line rounded-md p-0.5 w-fit">
        {["daily", "weekly"].map((v) => (
          <button key={v} data-testid={`analytics-view-${v}`} onClick={() => setView(v)}
            className={`px-3 py-1 rounded text-[10px] font-mono uppercase tracking-wider transition ${
              view === v ? "bg-vts-blue text-white" : "text-vts-silver/70 hover:text-white"}`}>
            {v}
          </button>
        ))}
      </div>
      {view === "daily" ? (
        <BreakdownTable rows={data?.daily || []} keyField="day" label="Daily breakdown · last 14 days" testId="analytics-daily-table" />
      ) : (
        <BreakdownTable rows={data?.weekly || []} keyField="week_start" label="Weekly breakdown · last 8 weeks" testId="analytics-weekly-table" />
      )}

      {/* TOP USERS BY VOLUME */}
      <div data-testid="analytics-top-users" className="vts-card p-4">
        <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-semibold mb-2 flex items-center gap-1.5">
          <Users className="h-3 w-3" /> Volume per account · last 30 days
        </div>
        {(data?.top_users || []).length === 0 ? (
          <p className="text-xs text-vts-silver/50 font-mono">No activity yet</p>
        ) : (
          <div className="space-y-1">
            <div className="grid grid-cols-[1fr_55px_75px_75px_75px_70px] gap-2 text-[9px] uppercase tracking-wider text-vts-silver/45 font-mono pb-1 border-b border-vts-line">
              <span>Account</span><span className="text-right">Trades</span><span className="text-right">Today $</span><span className="text-right">Week $</span><span className="text-right">30d Vol $</span><span className="text-right">Markup $</span>
            </div>
            {data.top_users.map((u) => (
              <div key={u.account_id} className="grid grid-cols-[1fr_55px_75px_75px_75px_70px] gap-2 items-center text-[11px] font-mono">
                <span className="text-white truncate flex items-center gap-1.5">
                  {u.account_id}
                  {u.is_demo && <span className="text-[8px] px-1 rounded bg-vts-line text-vts-silver/50">DEMO</span>}
                </span>
                <span className="text-right text-vts-silver/70">{u.trades}</span>
                <span className="text-right text-vts-silver/80">${fmt(u.volume_today)}</span>
                <span className="text-right text-vts-silver/80">${fmt(u.volume_week)}</span>
                <span className="text-right text-white">${fmt(u.volume)}</span>
                <span className={`text-right ${u.markup > 0 ? "text-vts-profit" : "text-vts-silver/45"}`}>${fmt(u.markup)}</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[9px] text-vts-silver/45 font-mono mt-2">
          Markup is earned on REAL-money trades only ({data?.markup_pct ?? 3}% of payout). Demo volume shown for engagement tracking.
        </p>
      </div>
    </div>
  );
};

export default AdminAnalyticsPanel;
